<p-toast></p-toast>
@if (isLoading) { <p-progressBar mode="indeterminate"></p-progressBar> }
<app-page-shell>
  <app-page-hero eyebrow="Administration" title="Notification Teams"
    [subtitle]="'Saved employee audiences for ' + companyName + '.'">
    <div nav>
      <a pButton routerLink="/business-events/alerts" label="Alerts" icon="pi pi-bolt" [outlined]="true"
        severity="secondary"></a>
      <a pButton routerLink="/business-events/teams" label="Teams" icon="pi pi-users"></a>
      <a pButton routerLink="/business-events/templates" label="Templates" icon="pi pi-file-edit" [outlined]="true"
        severity="secondary"></a>
      <a pButton routerLink="/business-events/executions" label="Activity" icon="pi pi-history" [outlined]="true"
        severity="secondary"></a>
    </div>
    <div actions>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true"
        (onClick)="load()"></p-button>
      <p-button label="New team" icon="pi pi-plus" (onClick)="openTeam()" [disabled]="!canManage"></p-button>
    </div>
  </app-page-hero>
  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>
  <app-page-data-panel title="Company notification teams"
    subtitle="A team is a saved, manually maintained group of active employees. It never crosses company boundaries."
    [resultText]="teams.length + ' teams'">
    <p-table [value]="teams" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll"
      [tableStyle]="{ 'min-width': '760px' }">
      <ng-template pTemplate="header">
        <tr>
          <th>Team</th>
          <th>Members</th>
          <th>Status</th>
          <th class="text-right">Manage</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-team>
        <tr>
          <td>
            <div class="stack"><strong>{{ team.teamName }}</strong><small>{{ team.active !== false ? 'Available to new
                alert actions' : 'Archived; kept only for audit' }}</small></div>
          </td>
          <td>{{ team.memberCount || 0 }} active employee{{ (team.memberCount || 0) === 1 ? '' : 's' }}</td>
          <td><p-tag [value]="team.active !== false ? 'ACTIVE' : 'ARCHIVED'"
              [severity]="team.active !== false ? 'success' : 'secondary'"></p-tag></td>
          <td class="text-right"><p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit team"
              (onClick)="openTeam(team)" [disabled]="!canManage"></p-button></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center p-4">No notification teams exist for this company.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>

<p-dialog [(visible)]="dialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [header]="form.id ? 'Edit notification team' : 'Create notification team'" [style]="{ width: 'min(1020px, 96vw)' }"
  [breakpoints]="{ '860px': '96vw' }" styleClass="team-create-dialog">
  <section class="team-dialog-body">
    <aside class="team-dialog-summary">
      <div class="summary-icon"><i class="pi pi-users"></i></div>
      <div>
        <small>COMPANY NOTIFICATION TEAM</small>
        <h3>{{ form.teamName.trim() || 'New notification team' }}</h3>
        <p>A saved audience of active employees for internal alert recipients. Membership is private to this company.
        </p>
      </div>
      <div class="team-dialog-facts">
        <div><span>Company</span><strong>{{ companyName }}</strong></div>
        <div><span>Selected members</span><strong>{{ selectedEmployees.length }}</strong></div>
        <div><span>Eligible employees</span><strong>{{ employees.length }}</strong></div>
        <div><span>Availability</span><strong>{{ form.active !== false ? 'Available to alerts' : 'Archived' }}</strong>
        </div>
      </div>
    </aside>

    <section class="team-dialog-form" aria-label="Notification team details">
      @if (error) { <div class="error-box team-wide-field">{{ error }}</div> }

      <p-inputGroup class="team-wide-field">
        <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
        <p-iftalabel>
          <input id="notificationTeamName" pInputText [(ngModel)]="form.teamName" autocomplete="off" />
          <label for="notificationTeamName">Team name *</label>
        </p-iftalabel>
      </p-inputGroup>

      <section class="employee-picker team-wide-field" aria-labelledby="team-members-heading">
        <div class="employee-picker-heading">
          <div>
            <strong id="team-members-heading">Active employees</strong>
            <span>Choose the employees who should receive alerts sent to this team.</span>
          </div>
          <span class="member-count">{{ selectedEmployees.length }} selected</span>
        </div>

        @if (employees.length) {
        <p-inputGroup class="employee-search">
          <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
          <input id="teamEmployeeSearch" pInputText [(ngModel)]="employeeSearch"
            placeholder="Find by name, employee number, email, or phone" autocomplete="off" />
        </p-inputGroup>

        <div class="employee-picker-list" role="list" aria-label="Available active employees">
          @for (employee of filteredEmployees; track employee.employeeId) {
          <button type="button" class="employee-option" [class.is-selected]="isEmployeeSelected(employee.employeeId)"
            (click)="toggleEmployee(employee.employeeId)">
            <span class="employee-check" aria-hidden="true"><i
                [class]="isEmployeeSelected(employee.employeeId) ? 'pi pi-check' : ''"></i></span>
            <span class="employee-avatar">{{ employeeInitials(employee) }}</span>
            <span class="employee-details">
              <strong>{{ employee.displayName }}</strong>
              <small>{{ employee.employeeNumber ? 'Employee ' + employee.employeeNumber : 'Active employee' }} &middot;
                {{ employeeContact(employee) }}</small>
            </span>
          </button>
          } @empty {
          <div class="employee-empty employee-search-empty">
            <i class="pi pi-search"></i>
            <strong>No employees match this search</strong>
            <span>Try a name, employee number, email address, or phone number.</span>
          </div>
          }
        </div>

        @if (selectedEmployees.length) {
        <div class="selected-members" aria-label="Selected employees">
          @for (employee of selectedEmployees; track employee.employeeId) {
          <button type="button" (click)="toggleEmployee(employee.employeeId)"
            [attr.aria-label]="'Remove ' + employee.displayName">
            {{ employee.displayName }} <i class="pi pi-times"></i>
          </button>
          }
        </div>
        } @else {
        <p class="employee-hint"><i class="pi pi-info-circle"></i> You can save an empty team and add members later. It
          will not send to anyone until it has members.</p>
        }
        } @else {
        <div class="employee-empty">
          <i class="pi pi-user-plus"></i>
          <strong>No active employees are available for {{ companyName }}</strong>
          <span>Notification teams can only contain active HR employees from this company.</span>
          <a pButton routerLink="/hr/employees/add" label="Add employee" icon="pi pi-plus" severity="secondary"
            [outlined]="true" (click)="dialogVisible = false"></a>
        </div>
        }
      </section>

      <label class="team-state team-wide-field">
        <p-toggleswitch [(ngModel)]="form.active"></p-toggleswitch>
        <span><strong>Team is active</strong><small>Active teams can be selected by new alert actions. Archive to keep
            this team for audit only.</small></span>
      </label>
    </section>
  </section>
  <ng-template pTemplate="footer">
    <div class="team-dialog-footer">
      <span class="footer-summary">{{ selectedEmployees.length }} member{{ selectedEmployees.length === 1 ? '' : 's' }}
        selected</span>
      <span class="footer-spacer"></span>
      <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true"
        (onClick)="dialogVisible = false"></p-button>
      <p-button [label]="form.id ? 'Save team' : 'Create team'" icon="pi pi-check" [loading]="isSaving"
        [disabled]="!canManage" (onClick)="save()"></p-button>
    </div>
  </ng-template>
</p-dialog>